import type { BadgeProps } from '@/shared/components/ui/badge'
import type {
	AlertInfoModel,
	AlertModel,
	AlertsInfoResponseModel,
	PricingCampaignModel,
} from '@/shared/generated'
import type { TFunction } from 'i18next'

import { filter, find } from 'lodash/fp'

import { AlertLevel } from '@/shared/generated'

export type AlertStyling = {
	backgroundClassNames: string
	badgeVariant: BadgeProps['variant']
}
type SolutionData = {
	pricingCampaigns: PricingCampaignModel[] | null
	alertsByPc: Record<string, string[]>
	optimizationGroupId: string
}
type GetPricingAlertsTreeProps = {
	pricingAlerts?: AlertsInfoResponseModel
	annotations?: AlertModel[]
	solutionData?: SolutionData
}
export const getAlertLevelStyling = (level: string): AlertStyling => {
	switch (level) {
		case AlertLevel.Warning:
			return {
				backgroundClassNames:
					'bg-yellow-50 border-yellow-200 dark:bg-yellow-950 dark:border-yellow-800',
				badgeVariant: 'warning',
			}
		case AlertLevel.Unassigned:
		case AlertLevel.Critical:
			return {
				backgroundClassNames:
					'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800',
				badgeVariant: 'destructive',
			}
		default:
			return { backgroundClassNames: '', badgeVariant: 'neutral' }
	}
}

/**
 * Get the pricing alerts tree from the alerts and annotations
 * @param pricingAlerts - The alerts
 * @param annotations - The annotations
 * @param solutionData - The solution data
 * @returns - The pricing alerts tree
 */
export function getPricingAlertsTree({
	pricingAlerts,
	annotations,
	solutionData,
}: GetPricingAlertsTreeProps) {
	return pricingAlerts?.by_level
		?.sort(
			sortedLevels([
				AlertLevel.Unassigned,
				AlertLevel.Critical,
				AlertLevel.Warning,
			])
		)
		?.map(
			({
				level,
				products,
				est_revenue,
				est_revenue_share,
			}: AlertInfoModel) => ({
				level: level!,
				products,
				est_revenue,
				est_revenue_share,
				alerts: filter({ level }, pricingAlerts?.by_id)?.map(
					({ id, products, est_revenue, est_revenue_share }) => {
						const alertAnnotation = find({ id }, annotations) as AlertModel

						return {
							id: id!,
							name: alertAnnotation?.name || id!,
							products,
							est_revenue,
							est_revenue_share,
							...(solutionData && {
								solution: {
									description: alertAnnotation?.description ?? '',
									tooltip: alertAnnotation?.solution_tooltip ?? '',
									pricingCampaigns:
										solutionData?.pricingCampaigns?.filter((pc) =>
											solutionData.alertsByPc?.[id!]?.includes(pc.id)
										) || null,
									optimizationGroupId: solutionData.optimizationGroupId,
								},
							}),
						}
					}
				),
			})
		)
}
const sortedLevels =
	(levels: AlertLevel[]) =>
	(a: AlertInfoModel | AlertModel, b: AlertInfoModel | AlertModel) => {
		const aIndex = levels.indexOf(a.level! as AlertLevel)
		const bIndex = levels.indexOf(b.level! as AlertLevel)

		if (aIndex > bIndex) return 1
		if (aIndex < bIndex) return -1

		return 0
	}
export const getAlertAnnotationDataChoices = (
	annotations: AlertModel[],
	t: TFunction
) =>
	annotations
		?.sort(
			sortedLevels([
				AlertLevel.Unassigned,
				AlertLevel.Critical,
				AlertLevel.Warning,
			])
		)
		.map((annotation) => alertAnnotationToDataChoice(annotation, t))
export const getAlertDataChoices = (annotations: AlertModel[], t: TFunction) =>
	annotations
		?.sort(
			sortedLevels([
				AlertLevel.Unassigned,
				AlertLevel.Critical,
				AlertLevel.Warning,
			])
		)
		?.map((alert) => ({
			label: alert.name?.includes(' ')
				? alert.name
				: t(alert.name, { defaultValue: alert.name }),
			value: alert.id,
		}))

export const alertAnnotationToDataChoice = (
	{
		id,
		name,
		level,
	}: {
		id: string
		name: string
		level: AlertLevel
	},
	t: TFunction
) => ({
	name: name?.includes(' ') ? name : t(name, { defaultValue: name }),
	id,
	level,
	//className: `text-${getAlertLevelStyling(level).badgeVariant}`,
})

export function getAlertVariant(level: string) {
	switch (level) {
		case AlertLevel.Warning:
			return 'warning'
		case AlertLevel.Unassigned:
		case AlertLevel.Critical:
			return 'destructive'
		default:
			return 'info'
	}
}
